import React from "react";
import { money } from "@/lib/api";
import {
    Package, Truck, MapPin, PackageCheck, CheckCircle2, Circle,
    RotateCcw, Ban, BadgeDollarSign, Clock,
} from "lucide-react";

const cx = (...a) => a.filter(Boolean).join(" ");

const SHIP_ICON = {
    LABEL_CREATED: Package,
    IN_TRANSIT: Truck,
    OUT_FOR_DELIVERY: MapPin,
    DELIVERED: PackageCheck,
};

const SHIP_LABEL = {
    LABEL_CREATED: "Label created",
    IN_TRANSIT: "In transit",
    OUT_FOR_DELIVERY: "Out for delivery",
    DELIVERED: "Delivered",
};

export function ShipmentStatusBadge({ status }) {
    const tone = status === "DELIVERED"
        ? "bg-success/10 text-success border-success/30"
        : "bg-electric/10 text-electric border-electric/30";
    const Icon = SHIP_ICON[status] || Truck;
    return (
        <span data-testid="shipment-status-badge" className={cx("inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-medium", tone)}>
            <Icon className="h-3 w-3" />{SHIP_LABEL[status] || status}
        </span>
    );
}

function fmt(at) {
    try { return new Date(at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
    catch { return ""; }
}

export function ShipmentTracker({ shipment }) {
    if (!shipment) return null;
    return (
        <div data-testid="shipment-tracker" className="rounded-xl border border-border bg-secondary/20 p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2 text-sm">
                    <Truck className="h-4 w-4 text-electric" />
                    <span className="font-medium">{shipment.carrier}</span>
                    <span className="text-muted-foreground">·</span>
                    <span className="font-mono text-xs text-muted-foreground">{shipment.tracking_number}</span>
                </div>
                <ShipmentStatusBadge status={shipment.status} />
            </div>

            <ol className="mt-4 space-y-0">
                {(shipment.events || []).map((e, i) => {
                    const Icon = SHIP_ICON[e.status] || Circle;
                    const last = i === shipment.events.length - 1;
                    return (
                        <li key={e.status} className="flex gap-3">
                            <div className="flex flex-col items-center">
                                <span className={cx("flex h-7 w-7 items-center justify-center rounded-full border", e.done ? "border-success bg-success/15 text-success" : "border-border bg-card text-muted-foreground")}>
                                    {e.done ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Icon className="h-3.5 w-3.5" />}
                                </span>
                                {!last && <span className={cx("w-px flex-1", e.done ? "bg-success/40" : "bg-border")} style={{ minHeight: 18 }} />}
                            </div>
                            <div className={cx("pb-3", e.done ? "" : "opacity-60")}>
                                <p className="text-sm font-medium leading-tight">{e.label}</p>
                                <p className="text-xs text-muted-foreground">{e.done ? fmt(e.at) : `Estimated ${fmt(e.at)}`}</p>
                            </div>
                        </li>
                    );
                })}
            </ol>

            {shipment.label_cost != null && (
                <p className="mt-1 text-xs text-muted-foreground">Label: {shipment.carrier} {shipment.service} · {money(shipment.label_cost)}</p>
            )}
        </div>
    );
}

const RETURN_TONE = {
    REQUESTED: "bg-warning/10 text-warning border-warning/30",
    APPROVED: "bg-electric/10 text-electric border-electric/30",
    RETURN_SHIPPED: "bg-electric/10 text-electric border-electric/30",
    DENIED: "bg-destructive/10 text-destructive border-destructive/30",
    REFUNDED: "bg-success/10 text-success border-success/30",
};
const RETURN_LABEL = {
    REQUESTED: "Return requested",
    APPROVED: "Return approved",
    RETURN_SHIPPED: "Return shipped back",
    DENIED: "Return denied",
    REFUNDED: "Refunded",
};
const RETURN_ICON = {
    REQUESTED: Clock, APPROVED: RotateCcw, RETURN_SHIPPED: Truck, DENIED: Ban, REFUNDED: BadgeDollarSign,
};

export function ReturnStatusBadge({ status }) {
    const Icon = RETURN_ICON[status] || RotateCcw;
    return (
        <span data-testid="return-status-badge" className={cx("inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-medium", RETURN_TONE[status] || "bg-secondary text-secondary-foreground border-border")}>
            <Icon className="h-3 w-3" />{RETURN_LABEL[status] || status}
        </span>
    );
}
